/**
 * Stampa foglio di giro (A4) — apre nuova finestra con HTML stilato.
 *
 * Blocco B5.3 — Esteso per giri operativi `source="zone"`:
 *  - banner "DA ZONE" se source=="zone"
 *  - mostra trasportatore assegnato e stato giro
 *  - colonna fasciaOrariaVisita
 *  - badge EXTRA accanto alle fermate frutto di override AGGIUNGI_EXTRA
 *  - CAP + città per ciascun indirizzo
 *  - sezione separata "Fermate saltate" in coda (non confusa con le attive)
 *
 * Backward-compat: la legacy continua a passare gli stessi parametri; i nuovi
 * campi sono opzionali e mostrati solo se presenti.
 */
import { FASCE_LABEL, FASCE_VISITA_LABEL, STATO_GIRO_LABEL } from "../../lib/constants";

function formatNumberIt(n) {
  if (n == null) return "-";
  return new Intl.NumberFormat("it-IT").format(n);
}

function esc(v) {
  if (v == null) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function isExtraStop(stop) {
  // Override `AGGIUNGI_EXTRA` con data_giro == data corrente è considerato extra
  const ovrs = stop.overrides || [];
  return ovrs.some((o) => o.tipo === "AGGIUNGI_EXTRA");
}

function isRecuperoStop(stop) {
  // B5.5 — AGGIUNGI_EXTRA creato dalla riprogrammazione di un Recupero
  // ha motivo prefissato con "Recupero:" (vedi routes/recuperi.py).
  const ovrs = stop.overrides || [];
  return ovrs.some(
    (o) => o.tipo === "AGGIUNGI_EXTRA" && (o.motivo || "").toLowerCase().startsWith("recupero"),
  );
}

function buildAddress(s) {
  const parts = [];
  if (s.indirizzo) parts.push(s.indirizzo);
  const tail = [];
  if (s.cap) tail.push(s.cap);
  if (s.citta) tail.push(s.citta);
  if (tail.length) parts.push(tail.join(" "));
  return parts.join(" — ");
}

export function openPrintSheet({ data, giorno, settimana, mezzoData, logo, source = "legacy" }) {
  const win = window.open("", "_blank");
  if (!win) return false;
  const dateLabel = new Date(data).toLocaleDateString("it-IT", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });

  const isZone = source === "zone";
  const attive = mezzoData.fermate || [];
  const saltate = mezzoData.fermateSaltate || [];

  // Riga tabella per ciascuna fermata attiva
  const renderRow = (s, i) => {
    const recupero = isRecuperoStop(s);
    const extra = !recupero && isExtraStop(s);
    const fasciaVisita = s.fasciaOrariaVisita && s.fasciaOrariaVisita !== "SEMPRE_APERTO"
      ? `<span class="tag tag-visita">${esc(FASCE_VISITA_LABEL[s.fasciaOrariaVisita] || s.fasciaOrariaVisita)}</span>`
      : "";
    const extraTag = recupero
      ? '<span class="tag tag-recupero">RECUPERO</span>'
      : (extra ? '<span class="tag tag-extra">EXTRA</span>' : "");
    return `
      <tr>
        <td class="num">${i + 1}</td>
        <td>
          <strong>${esc(s.ragioneSociale || "")}</strong> ${extraTag}
          <br/><span class="muted">${esc(buildAddress(s))}</span>
          ${s.zona ? `<br/><span class="muted">Zona: <strong>${esc(s.zona)}</strong></span>` : ""}
        </td>
        <td class="muted">
          ${esc(FASCE_LABEL[s.fasciaOraria] || "-")}
          ${fasciaVisita}
          ${s.orarioRitiro ? `<br/><span style="font-size:10px">${esc(s.orarioRitiro)}</span>` : ""}
        </td>
        <td>
          ${s.telefonoMobile ? `<strong>${esc(s.telefonoMobile)}</strong>` : ""}
          ${s.telefonoMobile && s.telefonoFisso ? "<br/>" : ""}
          ${s.telefonoFisso ? `<span class="muted">${esc(s.telefonoFisso)}</span>` : ""}
        </td>
        <td class="cell-input"></td>
        <td class="cell-input"></td>
        <td class="cell-input"></td>
        <td class="cell-input wide"></td>
      </tr>
    `;
  };

  const activeRows = attive.map(renderRow).join("");

  // Sezione separata fermate saltate (se presenti)
  const skippedSection = saltate.length > 0 ? `
    <div class="section-title" style="margin-top:14px">
      <span class="badge" style="background:#F57C00">FERMATE SALTATE — ${saltate.length}</span>
      <span class="muted" style="margin-left:8px">(escluse dal giro odierno)</span>
    </div>
    <table style="margin-top:4px">
      <thead>
        <tr><th>#</th><th>Cliente / Indirizzo</th><th>Telefono</th><th>Motivo</th></tr>
      </thead>
      <tbody>
        ${saltate.map((s, i) => `
          <tr style="background:#fff5e6">
            <td class="num">${i + 1}</td>
            <td>
              <strong>${esc(s.ragioneSociale || "")}</strong>
              <span class="tag tag-skip">SALTATA</span>
              <br/><span class="muted">${esc(buildAddress(s))}</span>
            </td>
            <td>${esc(s.telefonoMobile || s.telefonoFisso || "")}</td>
            <td class="cell-input"></td>
          </tr>
        `).join("")}
      </tbody>
    </table>
  ` : "";

  // Trasportatore + stato (campi del B5.3, sempre opzionali)
  const traspName = mezzoData.trasportatore
    ? `${mezzoData.trasportatore.nome || ""} ${mezzoData.trasportatore.cognome || ""}`.trim()
    : null;
  const statoLabel = mezzoData.stato ? (STATO_GIRO_LABEL[mezzoData.stato] || mezzoData.stato) : null;

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Foglio di giro ${esc(dateLabel)}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #0F172A; font-size: 11px; }
    h1 { font-size: 22px; margin: 0 0 4px; color: #1B5E20; font-weight: 800; }
    .meta { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #1B5E20; padding-bottom: 8px; margin-bottom: 12px; }
    .logo-block { display: flex; align-items: center; gap: 12px; }
    .logo-block .icon { width: 44px; height: 44px; border-radius: 10px; background: #1B5E20; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: 800; }
    .meta .left { font-size: 11px; color: #475569; }
    .meta .right { text-align: right; font-size: 11px; }
    .field { display: inline-block; margin-right: 14px; }
    .field span.k { font-weight: 700; text-transform: uppercase; font-size: 9px; letter-spacing: .08em; color: #64748b; display: block; }
    .field span.v { font-weight: 600; font-size: 13px; color: #0F172A; }
    table { width: 100%; border-collapse: collapse; font-size: 10px; }
    thead th { background: #1B5E20; color: #fff; padding: 6px 4px; text-align: left; text-transform: uppercase; font-size: 9px; letter-spacing: .05em; }
    tbody td { border: 1px solid #cbd5e1; padding: 8px 4px; vertical-align: top; }
    tbody tr:nth-child(even) td { background: #f8fafc; }
    td.num { text-align: center; font-weight: 700; width: 22px; background: #f1f5f9 !important; font-size: 12px; color: #1B5E20; }
    td.cell-input { height: 36px; }
    td.cell-input.wide { min-width: 80px; }
    .muted { color: #64748b; font-size: 9px; }
    .tag { display:inline-block; color:#fff; font-size:8px; padding:1px 5px; border-radius:3px; font-weight:700; margin-left:4px; vertical-align:middle; }
    .tag-skip { background: #F57C00; }
    .tag-extra { background: #F57C00; }
    .tag-visita { background: #1565C0; }
    .tag-recupero { background: #7B1FA2; }
    .footer { margin-top: 18px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 18px; font-size: 10px; }
    .footer .box { border-top: 1px solid #cbd5e1; padding-top: 6px; min-height: 50px; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; background: #1B5E20; color: #fff; font-size: 9px; font-weight: 700; text-transform: uppercase; }
    .source-banner { display: inline-block; background: #7B1FA2; color: #fff; font-size: 9px; font-weight: 700; text-transform: uppercase; padding: 2px 8px; border-radius: 999px; margin-left: 6px; letter-spacing: .05em; }
    .section-title { font-size: 11px; font-weight: 600; color: #475569; margin-bottom: 4px; }
  </style></head><body>
  <div class="meta">
    <div class="logo-block">
      <div class="icon">${esc((logo?.text || "OE").charAt(0).toUpperCase())}</div>
      <div class="left">
        <h1>${esc(logo?.text || "Foglio di giro")} ${isZone ? '<span class="source-banner">Da zone</span>' : ""}</h1>
        <div>${esc(logo?.subtitle || dateLabel)}</div>
        <div class="muted" style="margin-top:2px"><strong>${esc(dateLabel)}</strong> · ${esc(giorno)} · ${esc(settimana)}</div>
      </div>
    </div>
    <div class="right">
      <div class="field"><span class="k">Mezzo</span><span class="v">${esc(mezzoData.mezzo?.nome || "-")}</span></div>
      <div class="field"><span class="k">Targa</span><span class="v">${esc(mezzoData.mezzo?.targa || "-")}</span></div>
      <div class="field"><span class="k">Zona</span><span class="v">${esc(mezzoData.zona)}</span></div>
      ${statoLabel ? `<div class="field"><span class="k">Stato</span><span class="v">${esc(statoLabel)}</span></div>` : ""}
    </div>
  </div>
  <div style="margin-bottom: 8px; display:flex; gap:18px; align-items:flex-end;">
    <div class="field"><span class="k">Autista</span>
      ${traspName
        ? `<span class="v">${esc(traspName)}</span>`
        : '<span class="v" style="border-bottom:1px solid #94a3b8; min-width:160px; display:inline-block;">&nbsp;</span>'}
    </div>
    <div class="field"><span class="k">Km iniziali</span>
      ${mezzoData.kmIniziali != null
        ? `<span class="v">${esc(formatNumberIt(mezzoData.kmIniziali))}</span>`
        : '<span class="v" style="border-bottom:1px solid #94a3b8; min-width:80px; display:inline-block;">&nbsp;</span>'}
    </div>
    <div class="field"><span class="k">Km finali</span>
      ${mezzoData.kmFinali != null
        ? `<span class="v">${esc(formatNumberIt(mezzoData.kmFinali))}</span>`
        : '<span class="v" style="border-bottom:1px solid #94a3b8; min-width:80px; display:inline-block;">&nbsp;</span>'}
    </div>
    <div class="field" style="margin-left:auto">
      <span class="badge">${attive.length} fermate</span>
      ${saltate.length > 0 ? `<span class="badge" style="background:#F57C00; margin-left:4px">${saltate.length} saltate</span>` : ""}
      <span class="badge" style="background:#1565C0; margin-left:4px">${esc(formatNumberIt(mezzoData.totalKm))} km</span>
    </div>
  </div>
  <table>
    <thead>
      <tr><th>#</th><th>Cliente / Indirizzo</th><th>Fascia / Orario</th><th>Telefono</th><th>Kg</th><th>€/kg</th><th>Firma</th><th>Note</th></tr>
    </thead>
    <tbody>${activeRows || `<tr><td colspan="8" style="text-align:center; color:#94a3b8; padding:24px">Nessuna fermata attiva</td></tr>`}</tbody>
  </table>
  ${skippedSection}
  <div class="footer">
    <div><div class="muted">Firma autista</div><div class="box"></div></div>
    <div><div class="muted">Firma responsabile deposito</div><div class="box"></div></div>
    <div><div class="muted">Note generali</div><div class="box"></div></div>
  </div>
  <script>window.onload=()=>{setTimeout(()=>window.print(), 250)}</script>
  </body></html>`;
  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
}

/**
 * Costruisce URL Google Maps Directions robusto.
 *
 * @returns {{url: string|null, included: Array, excluded: Array<{stop, motivo}>, depositoExcluded: string|null}}
 *
 * Logica per ogni fermata:
 *  1. Se lat/lng validi → usa coordinate (sempre affidabile).
 *  2. Altrimenti normalizza l'indirizzo:
 *     - rimuovi CAP "interni" già presenti nel testo (evita duplicazione/conflitto)
 *     - usa `cittaEffettiva` se disponibile, sennò `citta`
 *     - appendi CAP + città + "Italia" senza duplicare
 *  3. Una fermata è SOSPETTA (esclusa) se:
 *     - non ha lat/lng E
 *     - indirizzo < 8 char dopo trim, OR
 *     - termina con "..." OR ",..." (troncato/da verificare), OR
 *     - contiene pattern civico+sigla ambiguo `\d+/[A-Z]{3,}` (es. "1/GHIL")
 *
 * Deposito (origin/destination):
 *  - Incluso SOLO se ha (a) coordinate precise — almeno 5 decimali su lat E lng —
 *    OPPURE (b) indirizzo testuale completo: indirizzo + CAP 5-cifre + città.
 *  - Altrimenti escluso: il percorso parte/finisce dalla prima/ultima fermata valida
 *    e `depositoExcluded` contiene il motivo (per warning lato chiamante).
 */
function _sanitizeIndirizzo(raw) {
  if (!raw) return "";
  let s = String(raw).trim();
  // Rimuovi CAP interno (5 cifre standalone) per evitare conflitto con il CAP del record
  s = s.replace(/\b\d{5}\b/g, "");
  // Collassa spazi multipli e virgole pendenti
  s = s.replace(/\s+/g, " ").replace(/\s*,\s*,\s*/g, ", ").replace(/[,\s]+$/, "").trim();
  return s;
}

function _isSospetta(stop) {
  const lat = Number(stop?.lat);
  const lng = Number(stop?.lng);
  const hasCoord = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
  if (hasCoord) return null;  // ok, useremo coordinate
  const ind = String(stop?.indirizzo || "").trim();
  if (ind.length < 8) return "indirizzo troppo corto";
  if (ind.endsWith("...") || ind.endsWith(",..") || ind.endsWith("..")) return "indirizzo troncato";
  if (/\d+\/[A-Z]{3,}/.test(ind)) return "indirizzo ambiguo (civico+sigla)";
  return null;
}

function _decimalsOf(n) {
  // numero di cifre decimali nella rappresentazione JSON del numero
  if (n == null) return 0;
  const s = String(n);
  const i = s.indexOf(".");
  return i < 0 ? 0 : s.length - i - 1;
}

function _depositoSegment(deposito) {
  // Ritorna { seg, motivoEsclusione } — seg è null se il deposito va escluso.
  if (!deposito) return { seg: null, motivoEsclusione: "deposito non configurato" };
  const lat = Number(deposito.lat);
  const lng = Number(deposito.lng);
  const hasCoord = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
  // (a) Coordinate precise: ≥ 5 decimali sia su lat sia su lng
  if (hasCoord && _decimalsOf(deposito.lat) >= 5 && _decimalsOf(deposito.lng) >= 5) {
    return { seg: `${lat},${lng}`, motivoEsclusione: null };
  }
  // (b) Indirizzo testuale completo: indirizzo + CAP 5 cifre + città
  const ind = _sanitizeIndirizzo(deposito.indirizzo);
  const c = String(deposito.citta || "").trim();
  const cap = String(deposito.cap || "").trim();
  const hasFullText = ind.length >= 5 && /^\d{5}$/.test(cap) && c.length > 0;
  if (hasFullText) {
    const encode = (s) => encodeURIComponent(s).replace(/%20/g, "+");
    const parts = [ind, cap];
    if (!ind.toLowerCase().includes(c.toLowerCase())) parts.push(c);
    parts.push("Italia");
    return { seg: encode(parts.join(", ")), motivoEsclusione: null };
  }
  // Né coordinate precise né indirizzo completo → escluso
  const motivo = hasCoord
    ? "coordinate imprecise (richiesti ≥ 5 decimali)"
    : (ind || c || cap) ? "indirizzo deposito incompleto (richiesti indirizzo + CAP + città)" : "deposito senza coordinate né indirizzo";
  return { seg: null, motivoEsclusione: motivo };
}

export function buildGoogleMapsUrl(deposito, fermate) {
  const all = fermate || [];
  const included = [];
  const excluded = [];
  for (const s of all) {
    const motivo = _isSospetta(s);
    if (motivo) excluded.push({ stop: s, motivo });
    else included.push(s);
  }
  if (included.length === 0) {
    return { url: null, included, excluded, depositoExcluded: null };
  }

  const encode = (s) => encodeURIComponent(s).replace(/%20/g, "+");

  const segmentForStop = (s) => {
    const lat = Number(s?.lat);
    const lng = Number(s?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
      return `${lat},${lng}`;
    }
    // Indirizzo testuale: sanitize + componi
    const ind = _sanitizeIndirizzo(s.indirizzo);
    const c = (s.cittaEffettiva || s.citta || "Firenze").trim();
    const hasCitta = ind.toLowerCase().includes(c.toLowerCase());
    const cap = String(s.cap || "").trim();
    // CAP è già stato rimosso dall'indirizzo da _sanitizeIndirizzo, lo aggiungiamo se valido
    const parts = [ind];
    if (cap && /^\d{5}$/.test(cap)) parts.push(cap);
    if (!hasCitta && c) parts.push(c);
    parts.push("Italia");
    return encode(parts.filter(Boolean).join(", "));
  };

  const { seg: depSeg, motivoEsclusione: depositoExcluded } = _depositoSegment(deposito);
  const segments = [
    ...(depSeg ? [depSeg] : []),
    ...included.map(segmentForStop),
    ...(depSeg ? [depSeg] : []),
  ];

  return {
    url: `https://www.google.com/maps/dir/${segments.join("/")}`,
    included,
    excluded,
    depositoExcluded,
  };
}
